import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-bootstrap/Modal'
import Tabs from 'duties/shared/tabs'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import InputGroup from 'react-bootstrap/InputGroup'
import moment from 'moment'
import renderDescription from 'shared/render-description'
import RecipePlainSummary from 'analytics/RecipePlainSummary'
import MutedText from 'shared/forms/muted-text'
import {
  useMonitoringAnalytics,
  useUpdateMonitoringAnalytic
} from './use-monitoring-analytics'
import {
  useMonitoringExclusions,
  useDeleteMonitoringExclusion
} from './use-monitoring-exclusions'

const formatDate = (value) =>
  value ? moment(value).format('MMM D, YYYY') : ''

function ControlsTab({ enabled, sectionId }) {
  const { data, isLoading } = useMonitoringAnalytics({
    options: { enabled }
  })
  const { mutate, isLoading: isSaving } = useUpdateMonitoringAnalytic()

  const allRows = data?.monitoring_analytics || []
  const rows = sectionId
    ? allRows.filter((row) => row.section_id === sectionId)
    : allRows

  const grouped = useMemo(() => {
    const map = new Map()
    rows.forEach((row) => {
      const key = row.section_name || row.section_abbreviation
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(row)
    })
    return [...map.entries()]
  }, [rows])

  const handleToggle = (row) => (e) => {
    mutate({
      id: row.id,
      analyticId: row.analytic_id,
      isControl: e.target.checked
    })
  }

  return (
    <div>
      <p className="text-muted small">
        Toggle which analytics are treated as <strong>controls</strong>{' '}
        on the monitoring page. Changes apply to every user in this
        municipality and take effect immediately.
      </p>
      <div
        className="small border rounded p-2 mb-3"
        style={{ background: '#fff8e1', borderColor: '#fde68a' }}
      >
        <strong>Don't see an analytic you expected?</strong> Only 'active' analytics
        with a non-<em>Off</em> weight appear here.
        Go to the Analytics page to manage an analytics' weights.
      </div>

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      )}

      {!isLoading && grouped.length === 0 && (
        <div className="text-center text-muted py-4">
          No analytics available.
        </div>
      )}

      {!isLoading &&
        grouped.map(([sectionName, items]) => (
          <div key={sectionName} className="mb-4">
            <div
              className="text-muted fw-bold text-uppercase small mb-2"
              style={{ letterSpacing: '0.06em' }}
            >
              {sectionName}
            </div>
            <div className="d-flex flex-column">
              {items.map((row, idx) => (
                <div
                  key={row.analytic_id}
                  className={`d-flex align-items-center gap-3 py-3 ${
                    idx > 0 ? 'border-top' : ''
                  }`}
                >
                  <Form.Check
                    type="switch"
                    id={`mona-${row.analytic_id}`}
                    checked={row.is_control}
                    onChange={handleToggle(row)}
                    disabled={isSaving}
                  />
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{row.analytic_name}</div>
                    {/* Custom analytics describe themselves through their
                        recipe (config + table-name badges); core analytics
                        keep their curated marker-tagged description. */}
                    {row.recipe ? (
                      <MutedText as="div" className="mt-1">
                        <RecipePlainSummary
                          recipe={row.recipe}
                          predicates={row.recipe_predicates}
                        />
                      </MutedText>
                    ) : (
                      row.analytic_description && (
                        <MutedText as="div" className="mt-1">
                          {renderDescription(
                            row.analytic_description,
                            row.column_sources
                          )}
                        </MutedText>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  )
}

ControlsTab.propTypes = {
  enabled: PropTypes.bool.isRequired,
  sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

function ExclusionsTab({ enabled, sectionId }) {
  const { data, isLoading } = useMonitoringExclusions({
    params: sectionId ? { section_id: sectionId } : {},
    options: { enabled }
  })
  const { mutate: deleteExclusion, isLoading: isDeleting } =
    useDeleteMonitoringExclusion()

  const exclusions = data?.exclusions || []

  return (
    <div>
      <p className="text-muted small">
        Vendors and employees that have been excluded from specific control
        analytics. Transactions matching these will not appear on the
        monitoring page. Remove an exclusion to resume flagging.
      </p>

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      )}

      {!isLoading && exclusions.length === 0 && (
        <div className="text-center text-muted py-4">
          No exclusions for this dataset.
        </div>
      )}

      {!isLoading && exclusions.length > 0 && (
        <Table size="sm" hover>
          <thead>
            <tr>
              <th>Analytic</th>
              <th>Target</th>
              <th>Reason</th>
              <th>Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {exclusions.map((exclusion) => (
              <tr key={exclusion.id}>
                <td>{exclusion.analytic_name}</td>
                <td>
                  <div className="small text-muted">
                    {exclusion.excludable_type}
                  </div>
                  <div>{exclusion.excludable_name || 'Unknown'}</div>
                </td>
                <td className="small">{exclusion.reason}</td>
                <td className="small">
                  <div>{formatDate(exclusion.created_at)}</div>
                  {exclusion.created_by?.name && (
                    <div className="text-muted">
                      {exclusion.created_by.name}
                    </div>
                  )}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="light"
                    className="text-danger"
                    disabled={isDeleting}
                    onClick={() =>
                      deleteExclusion({ exclusionId: exclusion.id })
                    }
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

ExclusionsTab.propTypes = {
  enabled: PropTypes.bool.isRequired,
  sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

function RiskScoreTab({ riskThreshold, onRiskThresholdChange }) {
  return (
    <div>
      <p className="text-muted small">
        Only transactions with a risk score greater than or equal to this
        threshold are shown on the monitoring page.
      </p>
      <Form.Group controlId="configure-risk-threshold" style={{ maxWidth: '260px' }}>
        <Form.Label className="text-muted small fw-bold text-uppercase mb-1">
          Risk score threshold
        </Form.Label>
        <InputGroup>
          <InputGroup.Text>≥</InputGroup.Text>
          <Form.Control
            type="number"
            step="0.5"
            min="0"
            value={riskThreshold}
            onChange={(e) => onRiskThresholdChange(e.target.value)}
          />
        </InputGroup>
      </Form.Group>
    </div>
  )
}

RiskScoreTab.propTypes = {
  riskThreshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onRiskThresholdChange: PropTypes.func.isRequired
}

function ConfigureModal({
  show,
  onHide,
  initialTab,
  sectionId,
  showRiskScore = true,
  riskThreshold,
  onRiskThresholdChange
}) {
  const [activeTab, setActiveTab] = useState(initialTab || 'controls')

  useEffect(() => {
    if (show && initialTab) setActiveTab(initialTab)
  }, [show, initialTab])

  // No risk tab without risk ranking, or initialTab 'risk' opens on a headless tab.
  useEffect(() => {
    if (!showRiskScore && activeTab === 'risk') setActiveTab('controls')
  }, [showRiskScore, activeTab])

  const tabs = [
    { value: 'controls', name: 'Controls' },
    { value: 'exclusions', name: 'Exclusions' },
    ...(showRiskScore ? [{ value: 'risk', name: 'Risk score' }] : [])
  ]

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Monitoring configuration</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <div className="mt-3">
          {activeTab === 'controls' && (
            <ControlsTab
              enabled={show && activeTab === 'controls'}
              sectionId={sectionId}
            />
          )}
          {activeTab === 'exclusions' && (
            <ExclusionsTab
              enabled={show && activeTab === 'exclusions'}
              sectionId={sectionId}
            />
          )}
          {showRiskScore && activeTab === 'risk' && (
            <RiskScoreTab
              riskThreshold={riskThreshold}
              onRiskThresholdChange={onRiskThresholdChange}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

ConfigureModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  initialTab: PropTypes.oneOf(['controls', 'exclusions', 'risk']),
  sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showRiskScore: PropTypes.bool,
  riskThreshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onRiskThresholdChange: PropTypes.func.isRequired
}

export default ConfigureModal
